import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import StartGameOverlay from './StartGameOverlay';
import RetroLoadingDots from './RetroLoadingDots';

const RetroPrecisionClicker = ({ 
  isActive = false, 
  onEnd,
  onGameComplete,
  showStartButton = false,
  isGenerating = false 
}) => {
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(20);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentPixel, setCurrentPixel] = useState(null);
  // Show start button only when explicitly requested (like in RetroArcade)
  const [waitingToStart, setWaitingToStart] = useState(showStartButton);
  const gameAreaRef = useRef(null);

  const colors = [
    '#FF00FF', // Hot pink
    '#FF6B00', // Orange
    '#00FF9F', // Neon green
    '#FFB800', // Amber
    '#00FFFF', // Cyan
    '#FF0000', // Red
  ];

  // Initialize game
  useEffect(() => {
    if (isActive && !gameStarted && !waitingToStart) {
      setScore(0);
      setTimeLeft(20);
      setGameStarted(true);

      requestAnimationFrame(() => {
        generateNewPixel();
      });
    }
  }, [isActive, waitingToStart]);

  // Game timer - only runs when game has actually started
  useEffect(() => {
    if (!gameStarted || waitingToStart) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setTimeout(() => {
            onEnd?.();
          }, 2000);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameStarted, waitingToStart, score]);

  const generateNewPixel = () => {
    if (!gameAreaRef.current) return;

    const rect = gameAreaRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    
    // Generate position within 60% of the center area
    const radius = Math.min(rect.width, rect.height) * 0.3;
    const angle = Math.random() * Math.PI * 2;
    const distance = Math.random() * radius;
    
    const x = centerX + Math.cos(angle) * distance;
    const y = centerY + Math.sin(angle) * distance;

    setCurrentPixel({
      x,
      y,
      color: colors[Math.floor(Math.random() * colors.length)]
    });
  };

  const handleClick = (e) => {
    if (timeLeft === 0 || !currentPixel || !gameAreaRef.current) return;

    const rect = gameAreaRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Get the actual pixel element
    const pixelElement = gameAreaRef.current.querySelector('.pixel-target');
    if (!pixelElement) return;

    const pixelRect = pixelElement.getBoundingClientRect();
    const pixelCenterX = pixelRect.left + pixelRect.width / 2 - rect.left;
    const pixelCenterY = pixelRect.top + pixelRect.height / 2 - rect.top;

    const distance = Math.sqrt(
      Math.pow(x - pixelCenterX, 2) + 
      Math.pow(y - pixelCenterY, 2)
    );

    if (distance < 12) { // Slightly larger hit radius for better UX
      setScore(prev => prev + 1);
      generateNewPixel();
    }
  };

  const handleStart = () => {
    setWaitingToStart(false);
  };

  return (
    <div className="absolute inset-0 rounded-lg overflow-hidden">
      <div className="absolute inset-0 bg-black" />
      
      <AnimatePresence>
        {waitingToStart ? (
          <StartGameOverlay 
            onStart={handleStart}
            gameName="Precision Clicker"
          />
        ) : (
          <div 
            ref={gameAreaRef}
            className="absolute inset-0 flex flex-col items-center justify-center"
            onClick={handleClick}
          >
            {/* Score */}
            <motion.div
              className="text-[72px] text-white font-['VT323'] mb-4"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
            >
              {score}
            </motion.div>

            {/* Target Pixel */}
            <AnimatePresence>
              {currentPixel && (
                <motion.div
                  className="pixel-target absolute w-2 h-2 rounded-full"
                  style={{
                    left: currentPixel.x,
                    top: currentPixel.y,
                    backgroundColor: currentPixel.color,
                    boxShadow: `0 0 10px ${currentPixel.color}`
                  }}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                />
              )}
            </AnimatePresence>

            {/* Instructions */}
            <div className="text-[18px] text-white/70 font-['VT323']">
              {timeLeft === 0 
                ? `Final Score: ${score} hits!` 
                : "Click the pixel targets!"}
            </div>
          </div>
        )}
      </AnimatePresence>


    </div>
  );
};

export default RetroPrecisionClicker; 